import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { pareceEscaneado, TIPOS_FONTE, TIPOS_FONTE_COM_TEXTO, type CategoriaResumo, type Duvida, type Fonte } from "@noesis/shared";
import { FalhaDaApi, pedir } from "../api.js";
import { Icone } from "../componentes/Icone.js";
import { MenuDeAcoes, SeletorSegmentado } from "../componentes/Controles.js";
import { enunciadoLegivel } from "../rotulos.js";
import { IconeConferido, IconeCriar, IconeDuvida, IconeFechar } from "../componentes/icones.js";
import { SeletorDeCategoria } from "../componentes/SeletorDeCategoria.js";
import { useTrilhaAtiva } from "../estado/trilhas.js";
import { useSessao } from "../estado/sessao.js";

/**
 * Fontes por categoria e dúvidas abertas — o outro lado da RN-08.
 *
 * A fonte fica na **categoria**, não no item: a dúvida é sobre o assunto, e a
 * mesma referência serve para todas as questões dele. Uma fonte cadastrada num
 * nível alto atende os descendentes — quem estuda "Normalização" alcança o
 * livro cadastrado em "Modelagem" sem cadastrá-lo de novo.
 */

const NOME_DO_TIPO: Record<(typeof TIPOS_FONTE)[number], string> = {
  livro: "Livro",
  video: "Vídeo",
  artigo: "Artigo",
  curso: "Curso",
  documentacao: "Documentação",
  prova: "Prova",
  material: "Material de aula",
  outro: "Outro",
};

type TipoComTexto = (typeof TIPOS_FONTE_COM_TEXTO)[number];
function temTexto(tipo: (typeof TIPOS_FONTE)[number]): tipo is TipoComTexto {
  return (TIPOS_FONTE_COM_TEXTO as readonly string[]).includes(tipo);
}

/**
 * O `tipo` que o rótulo digitado merece. O rótulo é livre ("Podcast"); o `tipo`
 * é o conjunto fechado que o resto do app conhece, e um rótulo que não bate com
 * nenhum vira `outro` sem perder o que a pessoa escreveu.
 */
function tipoDoRotulo(rotulo: string): (typeof TIPOS_FONTE)[number] {
  const chave = rotulo
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
  if (/^(video|videoaula|youtube)/.test(chave)) return "video";
  if (/^(livro|ebook|apostila)/.test(chave)) return "livro";
  if (/^artigo/.test(chave)) return "artigo";
  if (/^(curso|aula)/.test(chave)) return "curso";
  if (/^(documentacao|docs)/.test(chave)) return "documentacao";
  return "outro";
}

/** O nome do tipo que a lista mostra: o que a pessoa escreveu, ou o do tipo. */
function nomeDaFonte(f: Fonte): string {
  return f.rotulo ?? NOME_DO_TIPO[f.tipo];
}

/** As sugestões do campo de tipo do link — só sugestão, o campo é livre. */
const SUGESTOES_DE_LINK = ["Vídeo", "Livro", "Artigo", "Curso", "Podcast", "Documentação"];

type ModoDaFonte = TipoComTexto | "link";
const MODOS_DA_FONTE = [
  { valor: "prova", rotulo: "Prova" },
  { valor: "material", rotulo: "Material de aula" },
  { valor: "link", rotulo: "Link" },
] as const satisfies readonly { valor: ModoDaFonte; rotulo: string }[];

export function Fontes() {
  const trilha = useTrilhaAtiva();
  const [fontes, setFontes] = useState<Fonte[]>([]);
  const [duvidas, setDuvidas] = useState<Duvida[]>([]);
  const [categorias, setCategorias] = useState<CategoriaResumo[]>([]);
  const [editando, setEditando] = useState<Fonte | "nova" | null>(null);
  // O post-it que está descolando (9d): some ao fim da animação, e só então a
  // dúvida é resolvida no servidor.
  const [resolvendo, setResolvendo] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const navegar = useNavigate();
  // "Nova fonte" mora no cabeçalho do Acervo e chega aqui pela URL.
  useEffect(() => {
    if (params.get("nova") !== "1") return;
    setEditando("nova");
    const proximos = new URLSearchParams(params);
    proximos.delete("nova");
    setParams(proximos, { replace: true });
  }, [params, setParams]);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    if (!trilha) {
      setCarregando(false);
      return;
    }
    void Promise.all([pedir("fonte.listar"), pedir("duvida.listar"), pedir("categoria.listar")])
      .then(([f, d, c]) => {
        setFontes(f.fontes);
        setDuvidas(d.duvidas);
        setCategorias(c.categorias);
      })
      .catch((e: unknown) => setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível carregar."))
      .finally(() => setCarregando(false));
  }, [trilha]);

  // Prova e material viram miniatura (ADR 044); link, livro e vídeo seguem em linha.
  const folhas = fontes.filter((f) => temTexto(f.tipo));
  const links = fontes.filter((f) => !temTexto(f.tipo));
  function acoesDe(f: Fonte) {
    return (
      <MenuDeAcoes
        rotulo={`Mais ações de ${f.titulo}`}
        acoes={[
          // A prova vira itens pela conferência de Importar (ADR 045).
          ...(temTexto(f.tipo) && f.caracteres > 0
            ? [{ rotulo: "Importar questões", aoEscolher: () => navegar(`/acervo?aba=importar&extrair=${f.id}`) }]
            : []),
          { rotulo: "Editar", aoEscolher: () => setEditando(f) },
          {
            rotulo: "Excluir fonte",
            destrutiva: true,
            confirmacao: `Excluir a fonte "${f.titulo}"?`,
            aoEscolher: () => {
              void pedir("fonte.excluir", { params: { id: f.id } }).then((r) => setFontes(r.fontes));
            },
          },
        ]}
      />
    );
  }

  if (!trilha) {
    return (
      <div className="tela tela--ampla tela--painel">
        <div className="tela__corpo">
          <section className="estado">
            <h2 className="estado__titulo">Nenhuma trilha em uso</h2>
            <p className="estado__texto">Fontes pertencem às categorias de uma trilha.</p>
            <Link className="botao" to="/trilhas">
              Escolher uma trilha
            </Link>
          </section>
        </div>
      </div>
    );
  }

  const abertas = duvidas.filter((d) => !d.resolvida);

  return (
    <div className="tela tela--ampla tela--painel">
      <div className="tela__corpo">

        {erro && (
          <p className="campo__erro" role="alert">
            {erro}
          </p>
        )}

        {/* Dúvidas e fontes em duas colunas (ADR 039). */}
        <section className="duvidas-mao" aria-labelledby="duvidas-titulo">
          <h2 className="t-secao" id="duvidas-titulo">
            Dúvidas abertas ({abertas.length})
          </h2>
          {abertas.length === 0 ? (
            <p className="t-ajuda">Nenhuma dúvida aberta. A montagem da sessão segue a ordem normal.</p>
          ) : (
            <>
              <p className="t-ajuda">Enquanto uma dúvida está aberta, os itens da categoria dela vêm primeiro na montagem da sessão.</p>
              <ul className="duvidas-mao__grade">
                {abertas.map((d) => (
                  <li
                    key={d.id}
                    className={resolvendo === d.id ? "postit duvida-postit duvida-postit--descolando" : "postit duvida-postit"}
                    onAnimationEnd={() => {
                      if (resolvendo === d.id) {
                        setResolvendo(null);
                        void pedir("duvida.resolver", { params: { id: d.id }, corpo: { notaId: null } }).then((r) =>
                          setDuvidas(r.duvidas),
                        );
                      }
                    }}
                  >
                    <p className="t-anotacao-p duvida-postit__onde">{d.categoria.join(" > ")}</p>
                    {d.enunciado && <p className="postit__texto">{enunciadoLegivel(d.enunciado)}</p>}
                    {d.descricao && <p className="duvida-postit__sua">{d.descricao}</p>}
                    <button
                      type="button"
                      className="botao botao--secundario duvida-postit__resolver"
                      disabled={resolvendo !== null}
                      onClick={() => {
                        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
                          void pedir("duvida.resolver", { params: { id: d.id }, corpo: { notaId: null } }).then((r) =>
                            setDuvidas(r.duvidas),
                          );
                        } else setResolvendo(d.id);
                      }}
                    >
                      <Icone glifo="check" papel="acao" />
                      entendi, resolver
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        <section className="fontes-mao" aria-labelledby="fontes-titulo">
          <h2 className="t-secao" id="fontes-titulo">
            Fontes ({fontes.length})
          </h2>
          <p className="t-ajuda">Fonte cadastrada numa categoria vale também para as que estão dentro dela.</p>

          {carregando ? (
            <div className="esqueleto esqueleto--faixa" aria-busy="true" />
          ) : fontes.length === 0 ? (
            <div className="estado">
              <p className="estado__texto">
                Fonte é onde você vai quando não entendeu: o capítulo do livro, o vídeo, a página da documentação. Suba
                também uma prova ou material de aula: a IA gera questões no estilo dele.
              </p>
              <button className="botao" type="button" onClick={() => setEditando("nova")}>
                <Icone glifo={IconeCriar} papel="acao" />
                Cadastrar fonte
              </button>
            </div>
          ) : (
            <>
              {folhas.length > 0 && (
                <ul className="fontes-folhas">
                  {folhas.map((f) => (
                    <li key={f.id}>
                      <MiniaturaDeFonte fonte={f} aoAbrir={() => setEditando(f)} acoes={acoesDe(f)} />
                    </li>
                  ))}
                </ul>
              )}
              {links.length > 0 && (
                <ul className="fontes-mao__lista">
                  {links.map((f) => (
                    <li className="fonte-mao" key={f.id}>
                      <Icone
                        glifo={f.tipo === "video" ? "video" : f.tipo === "livro" ? "fontes" : temTexto(f.tipo) ? "pagina" : "link"}
                        papel="navegacao"
                        className="fonte-mao__icone"
                      />
                      <div className="fonte-mao__texto">
                        <p className="t-secao">{f.titulo}</p>
                        <p className="t-ajuda">
                          {[
                            f.categoria.join(" › "),
                            nomeDaFonte(f).toLocaleLowerCase("pt-BR"),
                            f.caracteres > 0 ? `${f.caracteres.toLocaleString("pt-BR")} caracteres` : null,
                            f.nota,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      </div>
                      <div className="fonte-mao__acoes">
                        {f.url && (
                          <a className="link-mao" href={f.url} target="_blank" rel="noopener noreferrer">
                            <Icone glifo="abrir_fora" papel="acao" tamanho="p" />
                            abrir
                          </a>
                        )}
                        {acoesDe(f)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>

        {editando && (
          <FormularioDeFonte
            fonte={editando === "nova" ? null : editando}
            categorias={categorias}
            aoFechar={() => setEditando(null)}
            aoSalvar={(lista) => {
              setFontes(lista);
              setEditando(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

/**
 * A miniatura de uma prova ou material — ADR 044.
 *
 * Uma folha pequena que diz o que foi subido sem abrir o texto: o selo do
 * tipo, o assunto, o título e a linha que a IA escreveu ao salvar. Sem essa
 * linha — IA fora do ar na hora —, mostra o começo do texto. A folha inteira
 * abre a fonte: o botão do título se estica por cima dela, e o menu fica acima.
 */
function MiniaturaDeFonte({ fonte: f, aoAbrir, acoes }: { fonte: Fonte; aoAbrir: () => void; acoes: ReactNode }) {
  const numeros = [
    f.questoes ? `${f.questoes} ${f.questoes === 1 ? "questão" : "questões"}` : null,
    f.caracteres >= 1000 ? `${Math.round(f.caracteres / 1000).toLocaleString("pt-BR")} mil caracteres` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <article className={`fonte-folha fonte-folha--${f.tipo}`}>
      <div className="fonte-folha__topo">
        <span className="fonte-folha__selo">{f.tipo === "prova" ? "Prova" : "Material"}</span>
        <span className="t-ajuda fonte-folha__onde">{f.categoria.join(" › ")}</span>
        <span className="fonte-folha__acoes">{acoes}</span>
      </div>
      <h3 className="t-secao fonte-folha__titulo">
        <button type="button" className="fonte-folha__abrir" onClick={aoAbrir}>
          {f.titulo}
        </button>
      </h3>
      {f.resumo ? (
        <p className="fonte-folha__resumo">{f.resumo}</p>
      ) : f.trecho ? (
        <p className="fonte-folha__resumo fonte-folha__resumo--trecho">{f.trecho}</p>
      ) : (
        <p className="t-ajuda">Sem texto ainda. Abra para converter o arquivo.</p>
      )}
      {numeros && <p className="t-ajuda fonte-folha__numeros">{numeros}</p>}
    </article>
  );
}

/**
 * O formulário mora num diálogo modal, e não no fim da tela.
 *
 * No fim da tela ele abria **abaixo da dobra** — em y=832 numa janela de 800
 * no celular, medido em 2026-09-26 —, e "Cadastrar fonte" parecia não fazer
 * nada. Modal também tira o formulário de baixo das duas colunas no desktop.
 */
function DialogoDeFonte({ titulo, aoFechar, children }: { titulo: string; aoFechar: () => void; children: ReactNode }) {
  const dialogo = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = dialogo.current;
    if (d && !d.open) d.showModal();
  }, []);
  return (
    <dialog
      ref={dialogo}
      className="folha-dialogo folha-dialogo--gaveta"
      aria-labelledby="fonte-dialogo-titulo"
      onClose={aoFechar}
      onClick={(e) => {
        if (e.target === dialogo.current) aoFechar();
      }}
    >
      <div className="folha-dialogo__topo">
        <h2 className="t-cartao" id="fonte-dialogo-titulo">
          {titulo}
        </h2>
        <button type="button" className="botao-icone folha-dialogo__fechar" aria-label="Fechar" onClick={aoFechar}>
          <Icone glifo={IconeFechar} papel="acao" />
        </button>
      </div>
      {children}
    </dialog>
  );
}

function FormularioDeFonte({
  fonte,
  categorias,
  aoFechar,
  aoSalvar,
}: {
  fonte: Fonte | null;
  categorias: CategoriaResumo[];
  aoFechar: () => void;
  aoSalvar: (fontes: Fonte[]) => void;
}) {
  const [titulo, setTitulo] = useState(fonte?.titulo ?? "");
  const [url, setUrl] = useState(fonte?.url ?? "");
  const [modo, setModo] = useState<ModoDaFonte>(fonte ? (temTexto(fonte.tipo) ? fonte.tipo : "link") : "prova");
  const [rotulo, setRotulo] = useState(fonte?.rotulo ?? (fonte && !temTexto(fonte.tipo) ? NOME_DO_TIPO[fonte.tipo] : ""));
  const [categoriaId, setCategoriaId] = useState(fonte?.categoriaId ?? categorias[0]?.id ?? "");
  const [nota, setNota] = useState(fonte?.nota ?? "");
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const { limites } = useSessao();
  // O texto da prova ou material. `null` enquanto o da fonte editada carrega:
  // a lista não traz o texto (RN-08 o carrega junto), e salvar antes de ele
  // chegar gravaria um campo vazio por cima.
  const [texto, setTexto] = useState<string | null>(fonte && fonte.caracteres > 0 ? null : "");
  const [convertendo, setConvertendo] = useState(false);
  const [avisoDoArquivo, setAvisoDoArquivo] = useState<string | null>(null);
  const comTexto = modo !== "link";

  useEffect(() => {
    if (!fonte || fonte.caracteres === 0) return;
    void pedir("fonte.texto", { params: { id: fonte.id } })
      .then((r) => setTexto(r.markdown))
      .catch(() => {
        setTexto("");
        setErro("Não foi possível carregar o texto desta fonte.");
      });
  }, [fonte]);

  async function converter(arquivo: File) {
    setErro(null);
    setAvisoDoArquivo(null);
    setConvertendo(true);
    try {
      if (arquivo.type === "application/pdf") {
        await converterPdf(arquivo);
      } else {
        acrescentar(await pedirConversao(await prepararFoto(arquivo, limites.fonteMaxArquivo)));
        setAvisoDoArquivo(`"${arquivo.name}" convertido. Confira o texto abaixo antes de salvar.`);
      }
      if (!titulo.trim()) setTitulo(arquivo.name.replace(/\.[a-z0-9]+$/i, ""));
    } catch (e2) {
      setErro(e2 instanceof FalhaDaApi || e2 instanceof ArquivoRecusado ? e2.message : "Não foi possível converter o arquivo.");
    } finally {
      setConvertendo(false);
    }
  }

  // Cada arquivo **soma** ao texto: uma prova fotografada vem página a página.
  function acrescentar(markdown: string) {
    setTexto((atual) => (atual?.trim() ? `${atual.trim()}

${markdown}` : markdown));
  }

  async function pedirConversao(corpo: ArquivoPronto): Promise<string> {
    return (await pedir("fonte.converter", { corpo: { ...corpo, tipo: comTexto ? modo : "material" } })).markdown;
  }

  /**
   * PDF digital é lido aqui, sem IA. O escaneado vai inteiro ao Gemini e, se
   * ele não puder, página por página como foto, pela visão da Groq — ADR 046.
   */
  async function converterPdf(arquivo: File) {
    const dados = await arquivo.arrayBuffer();
    const leitor = await import("../pdf.js");
    // O PDF.js transfere o buffer ao worker; a cópia fica para o envio ao Gemini.
    const carga = leitor.abrirPdf(dados.slice(0));
    const pdf = await carga.promise.catch(() => {
      throw new ArquivoRecusado("Não foi possível abrir este PDF. Ele pode estar protegido por senha ou corrompido.");
    });
    try {
      const lido = await leitor.textoDoPdf(pdf);
      if (!pareceEscaneado(lido, pdf.numPages)) {
        acrescentar(lido);
        setAvisoDoArquivo(`"${arquivo.name}" lido no seu aparelho, sem IA. Confira o texto abaixo antes de salvar.`);
        return;
      }

      if (dados.byteLength <= limites.fonteMaxArquivo) {
        setAvisoDoArquivo("PDF escaneado: lendo as imagens. Pode levar até um minuto.");
        try {
          acrescentar(await pedirConversao({ nome: arquivo.name, tipoMime: "application/pdf", base64: await emBase64(new Blob([dados])) }));
          setAvisoDoArquivo(`"${arquivo.name}" convertido. Confira o texto abaixo antes de salvar.`);
          return;
        } catch (e) {
          // Sem Gemini, ou ele fora: as páginas seguem como fotos. A cota do dia não volta tentando.
          if (!(e instanceof FalhaDaApi) || e.codigo === "ia_cota") throw e;
        }
      }

      for (let n = 1; n <= pdf.numPages; n++) {
        setAvisoDoArquivo(`PDF escaneado: lendo a página ${n} de ${pdf.numPages}…`);
        const pagina = await leitor.paginaComoJpeg(pdf, n, LADO_MAXIMO_DA_FOTO);
        if (!pagina) throw new ArquivoRecusado(`Não foi possível ler a página ${n}.`);
        const corpo = { nome: `${arquivo.name} (página ${n})`, tipoMime: "image/jpeg" as const, base64: await emBase64(pagina) };
        try {
          acrescentar(await pedirConversao(corpo).catch(async (e: unknown) => {
            // A visão da Groq gratuita lê ~1 página por minuto; passado o minuto, a cota volta.
            if (!(e instanceof FalhaDaApi) || e.codigo !== "ia_indisponivel") throw e;
            setAvisoDoArquivo(`Página ${n} de ${pdf.numPages}: o limite por minuto da IA chegou. Esperando um minuto…`);
            await new Promise((ok) => setTimeout(ok, ESPERA_DA_COTA_POR_MINUTO_MS));
            return pedirConversao(corpo);
          }));
        } catch (e) {
          const motivo = e instanceof FalhaDaApi || e instanceof ArquivoRecusado ? e.message : "Falha ao converter.";
          throw new ArquivoRecusado(
            n === 1 ? motivo : `${motivo} Parou na página ${n}; as ${n - 1} anteriores estão no texto. Mande o resto depois.`,
          );
        }
      }
      setAvisoDoArquivo(`"${arquivo.name}" convertido página a página. Confira o texto abaixo antes de salvar.`);
    } finally {
      void carga.destroy();
    }
  }

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setErro(null);
    // Trocar uma prova para "livro" apaga o texto; fonte sem texto não manda o campo.
    const markdown = comTexto ? (texto ?? undefined) : fonte && fonte.caracteres > 0 ? null : undefined;
    const corpo = {
      categoriaId,
      titulo,
      url: comTexto ? "" : url,
      tipo: comTexto ? modo : tipoDoRotulo(rotulo),
      rotulo: comTexto ? "" : rotulo,
      nota,
      markdown,
    };
    try {
      const r = fonte
        ? await pedir("fonte.editar", { params: { id: fonte.id }, corpo })
        : await pedir("fonte.criar", { corpo });
      aoSalvar(r.fontes);
    } catch (e2) {
      setErro(e2 instanceof FalhaDaApi ? e2.message : "Não foi possível salvar a fonte.");
      setEnviando(false);
    }
  }

  if (categorias.length === 0) {
    return (
      <DialogoDeFonte titulo="Ainda não há categorias" aoFechar={aoFechar}>
        <section className="pilha">
          <p className="estado__texto">
            As categorias nascem da importação. Importe um lote antes de cadastrar fontes — elas se penduram numa
            categoria, não no vazio.
          </p>
          <Link className="botao" to="/importar">
            Ir para importar
          </Link>
        </section>
      </DialogoDeFonte>
    );
  }

  return (
    <DialogoDeFonte titulo={fonte ? "Editar fonte" : "Nova fonte"} aoFechar={aoFechar}>
      <form className="pilha" onSubmit={(e) => void enviar(e)}>

        <div className="campo">
          <label className="campo__rotulo" htmlFor="f-categoria">
            Categoria
          </label>
          <SeletorDeCategoria
            id="f-categoria"
            categorias={categorias}
            valor={categoriaId === "" ? null : categoriaId}
            aoMudar={(v) => setCategoriaId(v ?? "")}
            rotuloVazio="Escolha uma…"
            required
          />
          <p className="campo__ajuda">Uma fonte aqui atende também as categorias dentro desta.</p>
        </div>

        <div className="campo">
          <label className="campo__rotulo" htmlFor="f-titulo">
            Título
          </label>
          <input id="f-titulo" className="campo__entrada" value={titulo} onChange={(e) => setTitulo(e.target.value)} required />
        </div>

        <div className="campo">
          <span className="campo__rotulo">O que é</span>
          <SeletorSegmentado
            rotulo="O que é a fonte"
            opcoes={MODOS_DA_FONTE}
            valor={modo}
            aoMudar={setModo}
            className="fonte-form__modo"
          />
        </div>

        {comTexto ? (
          <>
            <div className="campo">
              <label className="campo__rotulo" htmlFor="f-arquivo">
                Arquivo <span className="tinta-suave">(PDF ou foto)</span>
              </label>
              <input
                id="f-arquivo"
                className="campo__entrada"
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                disabled={convertendo || enviando}
                onChange={(e) => {
                  const a = e.target.files?.[0];
                  e.target.value = "";
                  if (a) void converter(a);
                }}
              />
              <p className="campo__ajuda" aria-live="polite">
                {convertendo
                  ? "Convertendo… um PDF longo pode levar até um minuto."
                  : (avisoDoArquivo ??
                    "O arquivo não fica guardado: vira texto, você confere, e só o texto é salvo. Prova fotografada: mande uma página de cada vez.")}
              </p>
            </div>

            <div className="campo">
              <label className="campo__rotulo" htmlFor="f-texto">
                Texto
              </label>
              <textarea
                id="f-texto"
                className="campo__entrada campo__area"
                value={texto ?? ""}
                onChange={(e) => setTexto(e.target.value)}
                rows={12}
                disabled={texto === null || convertendo}
                placeholder={texto === null ? "Carregando o texto…" : "Converta um arquivo ou cole o texto aqui."}
              />
              <p className="campo__ajuda">
                {(texto ?? "").length.toLocaleString("pt-BR")} de {limites.fonteMaxTexto.toLocaleString("pt-BR")} caracteres
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="campo">
              <label className="campo__rotulo" htmlFor="f-rotulo">
                Que tipo de link?
              </label>
              <input
                id="f-rotulo"
                className="campo__entrada"
                list="f-rotulo-sugestoes"
                maxLength={40}
                value={rotulo}
                onChange={(e) => setRotulo(e.target.value)}
                placeholder="Vídeo, podcast, artigo…"
              />
              <datalist id="f-rotulo-sugestoes">
                {SUGESTOES_DE_LINK.map((o) => (
                  <option key={o} value={o} />
                ))}
              </datalist>
            </div>
            <div className="campo">
              <label className="campo__rotulo" htmlFor="f-url">
                Endereço <span className="tinta-suave">(opcional)</span>
              </label>
              <input
                id="f-url"
                className="campo__entrada"
                type="url"
                inputMode="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://"
              />
            </div>
          </>
        )}

        <div className="campo">
          <label className="campo__rotulo" htmlFor="f-nota">
            Onde procurar <span className="tinta-suave">(opcional)</span>
          </label>
          <textarea
            id="f-nota"
            className="campo__entrada campo__area"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            rows={2}
            placeholder="capítulo 4, a partir da página 88"
          />
        </div>

        {erro && (
          <p className="campo__erro" role="alert">
            {erro}
          </p>
        )}

        <div className="linha">
          <button className="botao botao--secundario" type="button" onClick={aoFechar} disabled={enviando}>
            Cancelar
          </button>
          <button
            className="botao"
            type="submit"
            disabled={enviando || convertendo || !titulo.trim() || (comTexto && (texto ?? "").length > limites.fonteMaxTexto)}
          >
            <Icone glifo={IconeCriar} papel="acao" />
            {enviando ? (comTexto ? "Salvando e resumindo…" : "Salvando…") : "Salvar"}
          </button>
        </div>
      </form>
    </DialogoDeFonte>
  );
}

/** Um arquivo que a tela recusa antes de mandar, com a frase de por quê. */
class ArquivoRecusado extends Error {}

const LADO_MAXIMO_DA_FOTO = 2000;

/** O que `fonte.converter` recebe, já em base64. */
type ArquivoPronto = { nome: string; tipoMime: "application/pdf" | "image/jpeg"; base64: string };

/** Uma janela da cota por minuto dos provedores, mais folga. */
const ESPERA_DA_COTA_POR_MINUTO_MS = 65_000;

/**
 * A foto em base64, pronta para `fonte.converter`.
 *
 * **Foto é reduzida aqui**, antes de subir: foto de celular passa fácil de
 * 4 MB, que é o teto de imagem da Groq, e 2000 px no lado maior ainda deixam
 * o texto de uma prova legível. Página de PDF escaneado passa pelo mesmo teto.
 */
async function prepararFoto(arquivo: File, maxBytes: number): Promise<ArquivoPronto> {
  if (!arquivo.type.startsWith("image/")) throw new ArquivoRecusado("Mande um PDF ou uma foto (JPG, PNG ou WebP).");

  const imagem = await createImageBitmap(arquivo);
  const escala = Math.min(1, LADO_MAXIMO_DA_FOTO / Math.max(imagem.width, imagem.height));
  const tela = document.createElement("canvas");
  tela.width = Math.round(imagem.width * escala);
  tela.height = Math.round(imagem.height * escala);
  tela.getContext("2d")?.drawImage(imagem, 0, 0, tela.width, tela.height);
  imagem.close();
  const blob = await new Promise<Blob | null>((ok) => tela.toBlob(ok, "image/jpeg", 0.85));
  if (!blob) throw new ArquivoRecusado("Não foi possível ler esta imagem.");
  if (blob.size > maxBytes) throw new ArquivoRecusado("A imagem ficou grande demais mesmo reduzida.");
  return { nome: arquivo.name, tipoMime: "image/jpeg", base64: await emBase64(blob) };
}

function emBase64(arquivo: Blob): Promise<string> {
  return new Promise((ok, falhar) => {
    const leitor = new FileReader();
    leitor.onload = () => ok(String(leitor.result).replace(/^data:[^,]*,/, ""));
    leitor.onerror = () => falhar(new ArquivoRecusado("Não foi possível ler o arquivo."));
    leitor.readAsDataURL(arquivo);
  });
}
